import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { IconButton } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import type { FileBrowserSource } from "../fileBrowserSource";
import { ExtraFileUploadDialog } from "./ExtraFileUploadDialog";

/**
 * 추가 파일 업로드 진입점이다. 버튼과 대상 선택 다이얼로그로 이루어진다.
 *
 * 버튼을 누르면 파일 선택창 대신 다이얼로그를 연다. **대상 경로를 먼저 정하기 위해서다.**
 * 사용자는 트리에서 대상 폴더를 확인하고, 필요하면 새 폴더를 만든 뒤 파일을 고른다.
 *
 * 브라우저 사이드바(루트, `initialPath=""`)와 extra 폴더 행(`initialPath=folder.path`)이 함께 쓴다.
 * 노출 게이트(owner + write seam)는 호출부가 소유한다.
 */
export function ExtraFileUploadAction({
  source,
  datasetUuid,
  storageBackend,
  initialPath,
  ariaLabel,
  title,
  triggerText = "업로드",
  onPress,
  onUploaded,
}: {
  source: FileBrowserSource;
  datasetUuid: string;
  storageBackend?: DatasetStorageBackend;
  // 다이얼로그가 처음 고를 대상 폴더. 사용자가 안에서 바꿀 수 있다.
  initialPath: string;
  // 같은 화면에 업로드 버튼이 여러 개 있다(루트 + 폴더별). 그래서 버튼 이름으로 대상을 구분해야 한다.
  ariaLabel?: string;
  title?: string;
  // 루트 진입점은 목적을 모두 적고, 폴더 행의 좁은 액션은 기본 "업로드"를 쓴다.
  triggerText?: string;
  // 다이얼로그를 열기 직전에 호출한다. 올린 파일이 보일 위치로 화면을 미리 옮길 때 쓴다.
  onPress?: () => void;
  onUploaded: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton
        text={triggerText}
        size="sm"
        variant="surface"
        aria-label={ariaLabel}
        title={title}
        onClick={() => {
          onPress?.();
          setOpen(true);
        }}
      />
      {open ? (
        <ExtraFileUploadDialog
          source={source}
          datasetUuid={datasetUuid}
          storageBackend={storageBackend}
          initialPath={initialPath}
          onClose={() => setOpen(false)}
          onUploaded={onUploaded}
        />
      ) : null}
    </>
  );
}
